// MK, the member's master key (spec section 6.1): 32 random bytes made on the
// first device to enrol, with a keyId so ciphertext can name the generation
// that produced it. Every other key in the hierarchy is derived from or wrapped
// under it. The bytes are handled raw here because WebCrypto can only wrap an
// extractable key, and MK has to be wrapped to each approved device and under
// the recovery code; where the key store keeps it between launches is the key
// store's decision (section 6.2).
import { randomBytes, randomUuid } from './webcrypto';

export const MASTER_KEY_BYTES = 32;

/**
 * Whether `mk` is a master key at all: 32 bytes in a Uint8Array. Anything
 * else — null, a base64 string, an object read back from a corrupted record,
 * a buffer of the wrong length — is not one, and nothing that asks "does this
 * device hold the key" may answer yes on it.
 *
 * @param {unknown} mk
 * @returns {mk is Uint8Array}
 */
export function isMasterKey(mk) {
  const isBytes = Object.prototype.toString.call(mk) === '[object Uint8Array]';
  return isBytes && mk.length === MASTER_KEY_BYTES;
}

/**
 * @param {unknown} mk
 * @returns {asserts mk is Uint8Array}
 */
export function assertMasterKey(mk) {
  if (!isMasterKey(mk)) {
    throw new TypeError(`Master key must be ${MASTER_KEY_BYTES} bytes`);
  }
}

/**
 * @returns {{ keyId: string, mk: Uint8Array }}
 */
export function generateMasterKey() {
  return { keyId: randomUuid(), mk: randomBytes(MASTER_KEY_BYTES) };
}
